/**
 * Product Card Hover Gallery
 * -----------------------------------------------------------
 * Lazily initializes a per-card image slider on hover (desktop only).
 * Fetches remaining product images from /products/{handle}.js on the
 * first hover and caches the result per handle. Only ONE gallery plays
 * at a time — hovering a new card pauses + resets all others.
 *
 * Pagination bullets jump to the clicked index and keep autoplay running.
 * Card image is fully clickable via .gallery-link.
 *
 * Works with:
 *   - Infinite scroll (watches for new .product-gallery-wrapper nodes)
 *   - Multiple sections on the same page
 *   - Shopify theme editor section reload
 */
(function () {
  'use strict';

  if (typeof Swiper === 'undefined') {
    var waited = 0;
    var waitSwiper = setInterval(function () {
      waited += 100;
      if (typeof Swiper !== 'undefined') {
        clearInterval(waitSwiper);
        boot();
      } else if (waited > 5000) {
        clearInterval(waitSwiper);
        console.warn('[HoverGallery] Swiper never loaded');
      }
    }, 100);
  } else {
    boot();
  }

  function boot() {
    var DESKTOP = window.matchMedia('(min-width: 992px)').matches;
    var productCache = new Map();

    /* ---------------------------------------------------------
       Fetch images for a handle (cached)
    --------------------------------------------------------- */
    function fetchProductImages(handle) {
      if (productCache.has(handle)) return productCache.get(handle);

      var p = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (!data || !Array.isArray(data.images)) return [];
          return data.images.slice(1).map(function (src) {
            var sep = src.indexOf('?') !== -1 ? '&' : '?';
            return src + sep + 'width=400';
          });
        })
        .catch(function (err) {
          console.warn('[HoverGallery] fetch failed for', handle, err);
          return [];
        });

      productCache.set(handle, p);
      return p;
    }

    /* ---------------------------------------------------------
       Stop every gallery except the given wrapper
    --------------------------------------------------------- */
    function stopAllExcept(exceptWrapper) {
      document.querySelectorAll('.product-gallery-wrapper').forEach(function (w) {
        if (w === exceptWrapper) return;
        var s = w._hoverSwiper;
        if (!s) return;
        if (s.autoplay && s.autoplay.running) s.autoplay.stop();
        if (s.params && s.params.loop) s.slideToLoop(0, 200);
        else s.slideTo(0, 200);
      });
    }

    /* ---------------------------------------------------------
       Build the swiper for a wrapper (once)
    --------------------------------------------------------- */
    function buildGallery(wrapper) {
      var sliderEl = wrapper.querySelector('.product-gallery-swiper');
      if (!sliderEl) return Promise.resolve(null);

      var swiperWrapper = sliderEl.querySelector('.swiper-wrapper');
      var paginationEl  = wrapper.querySelector('.gallery-progress-pagination');
      var handle        = wrapper.dataset.productHandle;
      var alt           = wrapper.dataset.productAlt || '';

      if (!handle) return Promise.resolve(null);

      wrapper.dataset.galleryState = 'loading';

      return fetchProductImages(handle).then(function (urls) {
        if (!urls.length) {
          wrapper.dataset.galleryState = 'single';
          return null;
        }

        if (sliderEl.swiper && typeof sliderEl.swiper.destroy === 'function') {
          sliderEl.swiper.destroy(true, true);
        }

        var frag = document.createDocumentFragment();
        urls.forEach(function (url) {
          var slide = document.createElement('div');
          slide.className = 'swiper-slide';
          slide.innerHTML =
            '<img src="' + url + '" alt="' + alt +
            '" width="400" height="400" loading="lazy" decoding="async">';
          frag.appendChild(slide);
        });
        swiperWrapper.appendChild(frag);

        var swiper = new Swiper(sliderEl, {
          nested: true,
          eventPrefix: 'inner',
          observer: false,
          observeParents: false,
          resizeObserver: false,

          slidesPerView: 1,
          loop: false,
          rewind: true,
          speed: 450,
          allowTouchMove: false,
          simulateTouch: false,

          autoplay: {
            delay: 1200,
            disableOnInteraction: false
          },

          pagination: paginationEl
            ? { el: paginationEl, type: 'bullets', clickable: false }
            : undefined
        });

        swiper.autoplay.stop();

        wrapper._hoverSwiper = swiper;
        wrapper.dataset.galleryState = 'ready';

        if (wrapper.dataset.hovering === '1') {
          swiper.autoplay.start();
        }
        return swiper;
      });
    }

    /* ---------------------------------------------------------
       Bind hover handlers to a wrapper (once)
    --------------------------------------------------------- */
    function bindWrapper(wrapper) {
      if (!wrapper || wrapper.dataset.hoverBound === '1') return;
      wrapper.dataset.hoverBound = '1';

      wrapper.addEventListener('mouseenter', function () {
        wrapper.dataset.hovering = '1';

        stopAllExcept(wrapper);

        var state = wrapper.dataset.galleryState;

        if (state === 'ready') {
          wrapper._hoverSwiper.autoplay.start();
          return;
        }
        if (state === 'loading' || state === 'single') return;

        buildGallery(wrapper);
      });

      wrapper.addEventListener('mouseleave', function () {
        wrapper.dataset.hovering = '0';
        var s = wrapper._hoverSwiper;
        if (!s) return;
        s.autoplay.stop();
        if (s.params && s.params.loop) s.slideToLoop(0, 200);
        else s.slideTo(0, 200);
      });
    }

    /* ---------------------------------------------------------
       Scan a container for wrappers and bind
    --------------------------------------------------------- */
    function scan(container) {
      if (!DESKTOP) return;
      var root = container || document;
      root.querySelectorAll('.product-gallery-wrapper').forEach(bindWrapper);
    }

    scan(document);

    /* ---------------------------------------------------------
       Watch for infinite scroll / filter updates
    --------------------------------------------------------- */
    function getResultsContainer() {
      return (
        document.querySelector('results-list') ||
        document.querySelector('.product-grid') ||
        document.querySelector('[class*="product-grid"]') ||
        document.body
      );
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (!mutation.addedNodes.length) return;
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.product-gallery-wrapper')) {
            bindWrapper(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('.product-gallery-wrapper').forEach(bindWrapper);
          }
        });
      });
    });

    function attachObserver() {
      var container = getResultsContainer();
      if (!container) { setTimeout(attachObserver, 300); return; }
      observer.observe(container, { childList: true, subtree: true });
    }
    attachObserver();

    /* ---------------------------------------------------------
       Theme events
    --------------------------------------------------------- */
    document.addEventListener('products-loaded', function () { scan(document); });
    document.addEventListener('filters-changed', function () { scan(document); });
    document.addEventListener('shopify:section:load', function (e) {
      if (!e.target) return;
      scan(e.target);
    });

    /* ---------------------------------------------------------
       PAGINATION BULLET CLICKS
       - prevent product link navigation
       - lazy-init the gallery if not ready
       - jump to the clicked bullet index
       - keep autoplay running from the selected index
    --------------------------------------------------------- */
    document.addEventListener('click', function (e) {
      var bullet = e.target.closest('.gallery-progress-pagination .swiper-pagination-bullet');
      if (!bullet) return;

      // Stop the click from reaching the product link
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      var wrapper = bullet.closest('.product-gallery-wrapper');
      if (!wrapper) return;

      var sliderEl = wrapper.querySelector('.product-gallery-swiper');
      if (!sliderEl) return;

      // Determine index by bullet position
      var bullets = bullet.parentNode.querySelectorAll('.swiper-pagination-bullet');
      var index = Array.prototype.indexOf.call(bullets, bullet);
      if (index < 0) return;

      function jumpTo() {
        var s = sliderEl.swiper;
        if (!s || s.destroyed) return;
        // Go to the selected slide
        s.slideTo(index, 250);
        // Keep autoplay running — continues from the new index
        if (s.autoplay && !s.autoplay.running) s.autoplay.start();
      }

      var swiper = sliderEl.swiper;

      if (swiper && !swiper.destroyed) {
        jumpTo();
      } else if (wrapper.dataset.galleryState !== 'single' &&
                 wrapper.dataset.galleryState !== 'loading') {
        // Build on the fly, then jump + play
        wrapper.dataset.hovering = '1';
        buildGallery(wrapper).then(jumpTo);
      }
    }, true);

    document.addEventListener('pointerdown', function (e) {
      var bullet = e.target.closest('.gallery-progress-pagination .swiper-pagination-bullet');
      if (!bullet) return;
      e.stopPropagation();
    }, { capture: true, passive: true });
  }
})();